import {
  LayoutPanelState,
  LayoutDefinition as LayoutState,
} from "./LayoutRecord";
import { getLayoutSchema } from "./schemas";

export class InvalidLayoutDefinitionError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "InvalidLayoutDefinitionError";
  }
}

export class LayoutDefinition {
  readonly #state: LayoutState;
  readonly #schemaVersion: number;

  /**
   * Creates a new layout definition from the given object
   *
   * @throws {InvalidLayoutDefinitionError} if the object is not a valid layout definition
   * @throws {UnsupportedLayoutSchemaVersion} if the schema version is unsupported
   */
  static from_obj(obj: unknown, schemaVersion: number): LayoutDefinition {
    if (LayoutDefinition.isValid(obj, schemaVersion)) {
      return new LayoutDefinition(obj, schemaVersion);
    }
    throw new InvalidLayoutDefinitionError(
      "Input is not a valid layout definition",
    );
  }

  /**
   * Returns true if the given object is a valid layout definition, false otherwise
   *
   * @throws {UnsupportedLayoutSchemaVersion} if the schema version is unsupported
   */
  static isValid(obj: unknown, schemaVersion: number): obj is LayoutState {
    const schema = getLayoutSchema(schemaVersion);
    return schema.safeParse(obj).success;
  }

  constructor(layoutDefinition: LayoutState, schemaVersion: number);
  constructor();
  constructor(layoutDefinition?: LayoutState, schemaVersion?: number) {
    if (!layoutDefinition) {
      this.#state = {
        panels: {},
        layout: {
          id: "root",
          axis: "x",
          relativeSize: 1,
          children: [],
        },
        rules: [],
      };
      this.#schemaVersion = 1;
      return;
    }

    if (schemaVersion === undefined) {
      throw new Error(
        "Schema version must be provided when layout definition is provided",
      );
    }

    this.#state = layoutDefinition;
    this.#schemaVersion = schemaVersion;
  }

  /**
   * Returns the list of panels as an array of data references
   */
  get panels(): LayoutPanelState[] {
    return Object.values(this.#state.panels);
  }

  get rules(): LayoutState["rules"] {
    return this.#state.rules;
  }

  get state(): LayoutState {
    return this.#state;
  }

  get schemaVersion(): number {
    return this.#schemaVersion;
  }
}
